import { primaryCta } from '../lib/signup';

const STATIC_CTA = primaryCta('flight_static');

interface FlightLink {
  label: string;
  href: string;
}

export interface FlightSceneCopy {
  id: string;
  eyebrow?: string;
  title?: string;
  body?: string;
  tags?: readonly string[];
  cta?: { primary?: FlightLink; secondary?: FlightLink };
}

const SITE_LINKS: readonly FlightLink[] = [
  { label: 'Find out more about lykbl', href: '/home' },
  { label: STATIC_CTA.label, href: STATIC_CTA.href },
  { label: 'About', href: '/about' },
  { label: 'Help centre', href: '/help' },
  { label: 'Privacy policy', href: '/privacy' },
  { label: 'Terms of service', href: '/legal/terms' },
];

/**
 * Crawlable, visually hidden copy of the scroll-flight scenes for the
 * prerendered landing page. The flight engine builds its DOM client-side
 * only, so without this, non-JS crawlers would see an empty page.
 *
 * Server-only: the client never renders it (flight routes are replaced, not
 * hydrated — see main.tsx), because once the engine mounts it renders the same
 * scene copy visibly and a hidden duplicate would be noise for screen readers.
 */
export default function FlightStaticContent({ scenes }: { scenes: readonly FlightSceneCopy[] }) {
  if (typeof window !== 'undefined') return null;

  return (
    <div className="sr-only">
      {scenes.map((scene) => (
        <section key={scene.id} aria-labelledby={`flight-${scene.id}`}>
          {scene.eyebrow && <p>{scene.eyebrow}</p>}
          {scene.title && <h2 id={`flight-${scene.id}`}>{scene.title}</h2>}
          {scene.body && <p>{scene.body}</p>}
          {scene.tags && scene.tags.length > 0 && (
            <ul>
              {scene.tags.map((tag) => (
                <li key={tag}>{tag}</li>
              ))}
            </ul>
          )}
          {scene.cta?.primary && <a href={scene.cta.primary.href}>{scene.cta.primary.label}</a>}
          {scene.cta?.secondary && <a href={scene.cta.secondary.href}>{scene.cta.secondary.label}</a>}
        </section>
      ))}
      <nav aria-label="lykbl">
        <ul>
          {SITE_LINKS.map((link) => (
            <li key={link.href}>
              <a href={link.href}>{link.label}</a>
            </li>
          ))}
        </ul>
      </nav>
    </div>
  );
}
